import { useMemo } from 'react';
import { SchedulerDayColumn } from '@lp/features/Scheduler/types';
import { getShiftSiblingsMap } from '@lp/features/Scheduler/utils';
import { getSSiblingDataGetter } from '@lp/features/Scheduler/SchedulerSurgeons/utils';
import { Region, SResource, SResourcesWithShifts, SShiftType } from '@/api/apis/resources/schedule';

export const useSShiftSiblingMaps = (
  dayCols: SchedulerDayColumn[],
  userList: SResource[],
  shiftsData: SResourcesWithShifts,
  targetRegionId: string,
  regionsDictionary: Record<string, Region>,
) => {
  const siblingDataGetter = useMemo(
    () => getSSiblingDataGetter(targetRegionId, regionsDictionary),
    [targetRegionId, regionsDictionary],
  );

  const siblingMaps: Record<string, ReturnType<typeof getShiftSiblingsMap>> = useMemo(() => {
    return userList.reduce(
      (acc, u) => ({
        ...acc,
        [u.id]: getShiftSiblingsMap(dayCols, shiftsData[u.id].days, siblingDataGetter),
      }),
      {},
    );
  }, [userList, dayCols, shiftsData, siblingDataGetter]);

  return siblingMaps;
};

export const useSOnCallDayTotalsMap = (
  dayCols: SchedulerDayColumn[],
  userList: SResource[],
  shiftsData: SResourcesWithShifts,
  regionId: string,
) => {
  return useMemo(() => {
    const totals = new Map<string, number>();

    dayCols.forEach((column) => {
      let dayTotals = 0;

      userList.forEach((user) => {
        const shift = shiftsData[user.id].days[column.key];
        const isRelatedShift = shift?.regionId === regionId;
        if (shift?.shiftType === SShiftType.OnCall && isRelatedShift) dayTotals += 1;
      });

      totals.set(column.key, dayTotals);
    });

    return totals;
  }, [dayCols, regionId, shiftsData, userList]);
};
